/**
 * Browser Detection
 *
 * Pure functions for detecting browser version and checking support.
 * No side effects - all DOM/storage operations are in banner.ts.
 */

import type { BrowserCheckResult, BrowserVersion } from './types'

/** Browsers we explicitly check by name */
type SupportedBrowser =
  | 'Chrome'
  | 'Firefox'
  | 'Safari'
  | 'Mobile Safari'
  | 'Edge'
  | 'Opera'

/** Engines we check as fallback for derivative browsers */
type SupportedEngine = 'Blink' | 'Gecko'

/**
 * Minimum supported browser versions.
 * Based on "last 2 stable versions" policy for CSS Baseline compatibility.
 * Updated: February 2026
 */
const MIN_BROWSER_VERSIONS: Readonly<Record<SupportedBrowser, number>> = {
  Chrome: 130,
  Firefox: 134,
  Safari: 17,
  'Mobile Safari': 17, // iOS Safari - same threshold as desktop
  Edge: 130,
  Opera: 115
}

/**
 * Minimum supported engine versions.
 * Used as fallback for derivative browsers (Vivaldi, Brave, Waterfox, etc.)
 * - Blink version ≈ Chrome version
 * - Gecko version ≈ Firefox version
 *
 * Note: WebKit is NOT included here because Apple's UA string always reports
 * WebKit ~605.1.15 regardless of actual version. iOS/macOS control WebKit
 * updates system-wide, so we can't meaningfully check WebKit version.
 * WKWebView and other WebKit browsers will fall through to 'unknown' (no warning).
 */
const MIN_ENGINE_VERSIONS: Readonly<Record<SupportedEngine, number>> = {
  Blink: 130, // Same as Chrome
  Gecko: 134 // Same as Firefox
}

const isSupportedBrowser = (name: string): name is SupportedBrowser =>
  name in MIN_BROWSER_VERSIONS

const isSupportedEngine = (name: string): name is SupportedEngine =>
  name in MIN_ENGINE_VERSIONS

const parseMajorVersion = (version: string | undefined): BrowserVersion =>
  Number.parseInt(version ?? '0', 10) as BrowserVersion

/**
 * Check browser by name first, then fallback to engine check.
 * Uses dynamic import to avoid bundler conflicts with other ua-parser-js usages.
 */
export const checkBrowser = async (): Promise<BrowserCheckResult> => {
  const { UAParser } = await import('ua-parser-js')

  const parser = new UAParser()
  const browser = parser.getBrowser()
  const engine = parser.getEngine()

  const browserName = browser.name ?? 'Unknown'
  const browserVersion = parseMajorVersion(browser.version)

  // First: check by browser name (Chrome, Firefox, Safari, Mobile Safari, Edge, Opera)
  if (isSupportedBrowser(browserName)) {
    return browserVersion < MIN_BROWSER_VERSIONS[browserName]
      ? { status: 'outdated', browser: browserName, version: browserVersion }
      : { status: 'supported' }
  }

  // Fallback: check by engine (for Vivaldi, Brave, Waterfox, Samsung, etc.)
  // Note: WebKit is excluded - see comment above MIN_ENGINE_VERSIONS
  const engineName = engine.name ?? 'Unknown'
  const engineVersion = parseMajorVersion(engine.version)

  if (isSupportedEngine(engineName)) {
    return engineVersion < MIN_ENGINE_VERSIONS[engineName]
      ? { status: 'outdated', browser: browserName, version: engineVersion }
      : { status: 'supported' }
  }

  // Unknown browser/engine (including WKWebView) - don't warn
  return { status: 'unknown' }
}
